import { memo } from 'react'
import { useSelector } from 'react-redux'
import { useFirebaseConnect } from 'react-redux-firebase'

import clsx from 'clsx'

import { ReactComponent as EmojiNotHelpful } from 'assets/icons/icon-feedback-not-helpful.svg'
import { ReactComponent as EmojiHelpful } from 'assets/icons/icon-feedback-helpful.svg'

import { Button, Box, makeStyles, Divider } from '@material-ui/core'

import { useNotification, useAuth, UseBooleanType } from 'hooks'
import { logToAnalytics, setUserReaction } from 'modules'

type StylesProps = {
    color: string
}
const useStyles = makeStyles(theme => ({
    wrapper: {
        gap: 8
    },
    icon: {
        width: '24px',
        height: '24px',
        minWidth: '24px',
        cursor: 'pointer',
        color: '#828282',
        transition: 'transform 0.2s ease 0s',
        transform: 'translateY(2px)'
    },
    activeIcon: { color: '#ffca28', fill: 'currentColor' },
    label: {
        color: '#7f8c9a',
        lineHeight: 1,
        marginBottom: theme.spacing(2),
        alignItems: 'center'
    },
    iconContainer: {
        color: ({ color }: StylesProps) => color,
        justifyContent: 'flex-start',
        display: 'flex',
        alignItems: 'end',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        '&:hover svg': { fill: '#ffca28', transform: 'translateY(-1px)' }
    },
    disabled: {
        color: ({ color }: StylesProps) => `${color} !important`,
        backgroundColor: `transparent !important`,
        '&:hover': {
            backgroundColor: `transparent !important`
        }
    }
}))
export const notHelpfulReaction = 1
export const helpfulReaction = 2
export const minimalRatingToShow = 50

export const negativeAnswersList = [
    'Not what I was looking for',
    'The quality of the video was bad',
    "It's not up to date"
]
type Props = {
    showReactions: UseBooleanType
    playbookId: string
    showFinishStep: UseBooleanType
    color?: string
}
export const VideoReactions = memo(
    ({ showReactions, playbookId, showFinishStep, color = 'white' }: Props) => {
        const classes = useStyles({ color })

        const { showSuccessNotification } = useNotification()

        const { uid } = useAuth()

        useFirebaseConnect(`playbookReactions/${uid}/${playbookId}/reaction`)

        const { reaction } = useSelector(
            state => state.firebase.data.playbookReactions?.[uid]?.[playbookId]?.reaction || {}
        )

        const handleClick = (reaction: number) => {
            setUserReaction({
                uid,
                playbookId,
                reaction
            }).then(() => {
                showReactions.setTrue()

                showFinishStep.set(reaction === 2)

                showSuccessNotification('Your feedback was sent')

                logToAnalytics('video_reaction_sent', {
                    playbookId,
                    userUid: uid,
                    reaction: reaction === 1 ? 'not happy' : 'happy'
                })
            })
        }

        const isActive = (value: number) => reaction === value

        return (
            <Box
                display="flex"
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="end"
                className={classes.wrapper}
            >
                <Button
                    variant="text"
                    className={classes.iconContainer}
                    onClick={() => handleClick(helpfulReaction)}
                    disabled={isActive(helpfulReaction)}
                    classes={{ disabled: classes.disabled }}
                >
                    <EmojiHelpful
                        className={clsx(classes.icon, {
                            [classes.activeIcon]: isActive(helpfulReaction)
                        })}
                    />
                    <Box fontSize="12" fontWeight="400" color="#090C10">
                        Helpful
                    </Box>
                </Button>

                <Divider orientation="vertical" light={true} style={{ height: '18px' }} />

                <Button
                    variant="text"
                    className={classes.iconContainer}
                    onClick={() => handleClick(notHelpfulReaction)}
                    disabled={isActive(notHelpfulReaction)}
                    classes={{ disabled: classes.disabled }}
                >
                    <EmojiNotHelpful
                        className={clsx(classes.icon, {
                            [classes.activeIcon]: isActive(notHelpfulReaction)
                        })}
                    />
                    <Box fontSize="12" fontWeight="400" color="#090C10">
                        Not helpful
                    </Box>
                </Button>
            </Box>
        )
    }
)
